import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Loader2, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setResult(res.data);
    } catch {
      setResult({ message: "Something went wrong. Try again." });
    } finally {
      setLoading(false);
    }
  };

  const fullUrl = result?.reset_path ? `${window.location.origin}${result.reset_path}` : "";

  const copy = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 mb-8 text-sm text-[#6B7068] hover:text-[#2A2F2B]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to sign in
        </Link>
        <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 animate-fade-up">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg bg-[#2A2F2B] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#D18E63]" strokeWidth={2} />
            </div>
            <span className="font-heading text-2xl font-bold text-[#2A2F2B]">uptick</span>
          </div>
          <h1 className="font-heading text-3xl tracking-tight font-medium text-[#2A2F2B] mb-1">
            Forgot password?
          </h1>
          <p className="text-sm text-[#6B7068] mb-6">
            Enter your email and we'll generate a reset link.
          </p>

          {!result ? (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7068]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 rounded-lg"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] py-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-result">
              <p className="text-sm text-[#2A2F2B]">{result.message}</p>
              {result.reset_path ? (
                <>
                  <div className="rounded-xl bg-[#E8A25D]/10 border border-[#E8A25D]/30 p-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#E8A25D]">
                      Dev mode · No email provider configured
                    </div>
                    <p className="text-xs text-[#2A2F2B]">
                      In production, this link would be emailed. For now, copy &amp; use it directly:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-white p-2 rounded-lg border border-[#EAE6E1] font-mono-ticker truncate">
                        {fullUrl}
                      </code>
                      <button
                        onClick={copy}
                        data-testid="copy-reset-link"
                        className="p-2 rounded-lg bg-white border border-[#EAE6E1] hover:border-[#547A8F] transition-soft"
                      >
                        {copied ? <Check className="w-4 h-4 text-[#5C8D73]" /> : <Copy className="w-4 h-4 text-[#6B7068]" />}
                      </button>
                    </div>
                    <Link
                      to={result.reset_path}
                      data-testid="use-reset-link"
                      className="block text-center text-sm rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white py-2.5 transition-soft mt-2"
                    >
                      Open reset page
                    </Link>
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#6B7068]">
                  If an account exists for that email, you'll receive instructions.
                </p>
              )}
              <Link to="/login" className="block text-center text-sm text-[#547A8F] hover:underline">
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
